// features/workstations/dashboard-sections/sentiment.js
// ═══════════════════════════════════════════════════════════
// SENTIMENT — positioning, flow, retail tone
// ═══════════════════════════════════════════════════════════
// Tier C / context widget. Reads the sentiment slice of the
// trading bundle and renders:
//
//   FEAR & GREED    big gauge (0-100) with anchor labels
//   AAII            bulls / bears / spread (weekly retail)
//   COT             spec net positioning (crowded trade check)
//   SHORT INTEREST  SPY / IWM short % (squeeze fuel check)
//
// Composed from shared primitives so it visually slots next
// to Force Tracker, Today’s Read, and Pre-Market Read on
// any dashboard.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  PractitionerLens,
} from '../../../components/widgets/primitives';

// Map bundle tones — the bundle uses both 'mid' (legacy) and
// the standard 7-color tokens. Normalize here so PillRow always
// gets a valid tone.
function normalizeTone(tone) {
  const map = {
    mid:   'steel',
    dim:   'steel',
    gold:  'gold',
    sage:  'sage',
    terra: 'terra',
    amber: 'amber',
    teal:  'teal',
    purple: 'purple',
  };
  return map[tone] || 'steel';
}

// Fear & Greed value → tone (terra in extreme fear, gold in
// greed, terra again in extreme greed — the edges are both
// tail risk).
function fearGreedTone(value) {
  if (value == null) return 'steel';
  if (value < 25) return 'terra';  // extreme fear
  if (value < 45) return 'amber';
  if (value < 55) return 'steel';
  if (value < 75) return 'gold';
  return 'terra';                   // extreme greed
}

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function SentimentSection({ workstation, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/sentiment?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData(payload || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <section className="ws-sentiment">
        <div className="ws-sentiment-loading">Loading sentiment…</div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="ws-sentiment">
        <Eyebrow>sentiment — read failed</Eyebrow>
        <SerifSummary>{error || 'No data returned.'}</SerifSummary>
      </section>
    );
  }

  return <SentimentBody data={data} />;
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function SentimentBody({ data }) {
  const fg = data.fear_greed;
  const aaii = Array.isArray(data.aaii) ? data.aaii : [];
  const cot = Array.isArray(data.cot) ? data.cot : [];
  const shorts = Array.isArray(data.short_interest) ? data.short_interest : [];

  // Headline: drive from fear & greed label + AAII spread
  const fgLabel = fg?.label || 'neutral';
  const aaiiSpread = aaii.find(r => /spread/i.test(r.label || ''));
  const spreadText = aaiiSpread?.value || '';
  const headline = fg
    ? `${fgLabel}${spreadText ? ` — ${spreadText.split(' · ')[0]}` : ''}.`
    : 'Sentiment read.';

  // Summary: derived from sentiment lens preview if present
  const summary = data.lens?.preview
    || 'Positioning + flow across AAII, COT, and short interest.';

  return (
    <section className="ws-sentiment">
      <Eyebrow parts={[
        'sentiment',
        data.freshness?.source === 'live' ? 'live' : 'sample',
      ]} />

      <SerifHeadline>{headline}</SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {/* Fear & Greed gauge */}
      {fg && typeof fg.value === 'number' && (
        <SectionStrip label="fear & greed">
          <FearGreedGauge fg={fg} />
        </SectionStrip>
      )}

      {/* AAII (live) */}
      {aaii.length > 0 && (
        <SectionStrip label="aaii — retail sentiment (weekly)" count={aaii.length}>
          <PillTable>
            {aaii.map((r, i) => (
              <PillRow
                key={i}
                keyText={r.label || ''}
                valueText={r.value || ''}
                tone={normalizeTone(r.tone)}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* COT (sample until wired) */}
      {cot.length > 0 && (
        <SectionStrip label="cot — spec net positioning" count={cot.length}>
          <PillTable>
            {cot.map((r, i) => (
              <PillRow
                key={i}
                keyText={r.label || ''}
                valueText={r.value || ''}
                tone={normalizeTone(r.tone)}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* Short interest */}
      {shorts.length > 0 && (
        <SectionStrip label="short interest — squeeze fuel" count={shorts.length}>
          <PillTable>
            {shorts.map((r, i) => (
              <PillRow
                key={i}
                keyText={r.label || ''}
                valueText={r.value || ''}
                tone={normalizeTone(r.tone)}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* Practitioner lens */}
      {data.lens?.body && (
        <PractitionerLens label={data.lens.eyebrow || 'reading sentiment'}>
          <span dangerouslySetInnerHTML={{ __html: sanitizeLensHtml(data.lens.body) }} />
        </PractitionerLens>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// FEAR & GREED GAUGE
// ═══════════════════════════════════════════════════════════
function FearGreedGauge({ fg }) {
  const value = Math.max(0, Math.min(100, fg.value));
  const tone = fearGreedTone(value);
  return (
    <div className="ws-sentiment-gauge">
      <div className="ws-sentiment-gauge-track">
        <div
          className={`ws-sentiment-gauge-fill ws-sentiment-gauge-fill-${tone}`}
          style={{ width: `${value}%` }}
        />
        <div
          className="ws-sentiment-gauge-marker"
          style={{ left: `${value}%` }}
          aria-hidden
        />
      </div>
      <div className="ws-sentiment-gauge-labels">
        <span className="ws-sentiment-gauge-label-left">{fg.leftLabel || 'Extreme Fear'}</span>
        <span className={`ws-sentiment-gauge-value ws-sentiment-gauge-value-${tone}`}>
          {value} · {fg.label || '—'}
        </span>
        <span className="ws-sentiment-gauge-label-right">{fg.rightLabel || 'Extreme Greed'}</span>
      </div>
    </div>
  );
}

// Very lightweight sanitization: strip <script> and most html
// tags except em/strong (the two the bundle copy uses). We're
// dealing with server-seeded strings, not user input — this is
// belt-and-suspenders only.
function sanitizeLensHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<(?!\/?(em|strong)\b)[^>]+>/gi, '');
}
